import Cookies from "js-cookie";

// Single owner of the auth cookies + session-config persistence. Login,
// refresh and the SSO handoff all funnel through setTokens so cookie lifetime
// and session config can never drift between call sites.

export interface TokenPair {
  access_token: string;
  refresh_token?: string;
}

export interface SessionMeta {
  accessTokenExpiresIn?: number; // seconds
  refreshTokenExpiresIn?: number; // seconds
  session?: { idleTimeoutSec?: number; warningBeforeSec?: number };
}

const SESSION_CONFIG_KEY = "session-config";
const LAST_REFRESH_AT_KEY = "last-refresh-at";

const cookieOptions = (days: number) => ({
  expires: days,
  secure: window.location.protocol === "https:",
  sameSite: "Strict" as const,
});

const tokenStore = {
  setTokens(token: TokenPair, meta?: SessionMeta): void {
    // Cookie lives as long as the refresh token it carries (server-driven),
    // falling back to the historical 7 days when the backend doesn't say.
    const days = meta?.refreshTokenExpiresIn
      ? meta.refreshTokenExpiresIn / 86400
      : 7;
    Cookies.set("access-token", JSON.stringify(token), cookieOptions(days));
    if (token.refresh_token) {
      Cookies.set("refresh-token", token.refresh_token, cookieOptions(days));
    }
    if (meta?.accessTokenExpiresIn || meta?.session) {
      try {
        localStorage.setItem(
          SESSION_CONFIG_KEY,
          JSON.stringify({
            accessTokenExpiresIn: meta.accessTokenExpiresIn,
            idleTimeoutSec: meta.session?.idleTimeoutSec,
            warningBeforeSec: meta.session?.warningBeforeSec,
          })
        );
      } catch {
        /* storage unavailable — timers fall back to env/defaults */
      }
    }
    try {
      localStorage.setItem(LAST_REFRESH_AT_KEY, String(Date.now()));
    } catch {}
  },

  // The raw JSON cookie string — exactly what rides in the Authorization header.
  getAuthHeader(): string | undefined {
    return Cookies.get("access-token");
  },

  hasSession(): boolean {
    return !!Cookies.get("access-token");
  },

  // Written by whichever tab performed the last refresh (localStorage is
  // shared per-origin) — lets other tabs skip a redundant refresh call.
  getLastRefreshAt(): number {
    try {
      return parseInt(localStorage.getItem(LAST_REFRESH_AT_KEY) || "0", 10);
    } catch {
      return 0;
    }
  },

  getStoredSessionConfig(): {
    accessTokenExpiresIn?: number;
    idleTimeoutSec?: number;
    warningBeforeSec?: number;
  } | null {
    try {
      const raw = localStorage.getItem(SESSION_CONFIG_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },

  clearAll(): void {
    Cookies.remove("access-token");
    Cookies.remove("refresh-token");
    try {
      localStorage.removeItem(SESSION_CONFIG_KEY);
      localStorage.removeItem(LAST_REFRESH_AT_KEY);
    } catch {}
  },
};

export default tokenStore;
